import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Announcement } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox, CheckboxField } from '../../ui/checkbox';
import { Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { EmptyState, Panel } from '../../components/PageChrome';

export default function Announcements() {
  const { teamId } = useParams<{ teamId: string }>();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [form, setForm] = useState({
    title: '',
    body: '',
    audience: 'TEAM' as Announcement['audience'],
    email: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = async () => {
    if (!teamId) return;
    try {
      const res = await admin.listAnnouncements(teamId);
      setAnnouncements(res.announcements);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId) return;
    setBusy(true);
    setError(undefined);
    try {
      await admin.createAnnouncement(teamId, form);
      setForm({ title: '', body: '', audience: 'TEAM', email: true });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2}>Announcements</Heading>
        <Text className="mt-1 text-sm">Send one-way team updates with in-app and optional email notifications.</Text>
      </div>

      <Panel>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
            <Field>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </Field>
            <Field>
              <Label>Audience</Label>
              <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value as Announcement['audience'] })}>
                <option value="TEAM">Team</option>
                <option value="STAFF">Staff</option>
                <option value="PLAYERS">Players</option>
              </Select>
            </Field>
          </div>
          <Field>
            <Label>Message</Label>
            <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required />
          </Field>
          <CheckboxField>
            <Checkbox checked={form.email} onChange={(checked) => setForm({ ...form, email: checked })} />
            <Label>Email approved members who opted in</Label>
          </CheckboxField>
          <Button type="submit" color="dark/zinc" disabled={busy}>{busy ? 'Sending…' : 'Send announcement'}</Button>
        </form>
      </Panel>

      {error ? <Text className="text-red-400">{error}</Text> : null}

      {announcements.length === 0 ? (
        <EmptyState title="No announcements yet" body="Team updates will appear here after they are sent." />
      ) : (
        <ul className="space-y-3">
          {announcements.map((a) => (
            <li key={a.announcementId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium text-zinc-100">{a.title}</div>
                <Badge color="zinc">{a.audience}</Badge>
                {a.emailSent ? <Badge color="emerald">Email</Badge> : null}
              </div>
              <Text className="mt-1 text-xs">{new Date(a.createdAt).toLocaleString()}</Text>
              <Text className="mt-3 whitespace-pre-wrap text-sm">{a.body}</Text>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
